// 1. Imports React
// (Aucun import React nécessaire ici)

// 2. Imports externes
import {
  Building2,
  Clock,
  LayoutDashboard,
  TrendingDown,
  TrendingUp,
  Users,
} from "lucide-react";

// 3. Imports composants UI
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

// 4. Imports hooks personnalisés
import { useCompanies } from "@/hooks/useCompanies";
import { useGlobalStats } from "@/hooks/useStats";
import { WeeklyChart } from "@/components/charts/WeeklyChart";
import { MonthlyChart } from "@/components/charts/MonthlyChart";

// 5. Composant
export const SuperuserDashboardPage = () => {
  // ========== A. Custom Hooks ==========
  const {
    data: stats,
    isLoading: loading,
    error: errorStats,
  } = useGlobalStats();
  const {
    data: companiesData,
    isLoading: loadingCompanies,
    error: errorCompanies,
  } = useCompanies();
  const companies = companiesData?.companies || [];

  // ========== B. Données calculées ==========

  // 5 dernières entreprises créées
  const recentCompanies = companies
    .sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    )
    .slice(0, 5);

  // Fonction pour formater le changement en pourcentage
  const formatChange = (change: number, isToday: boolean = false): string => {
    const sign = change >= 0 ? "+" : "";
    const comparison = isToday ? "hier" : "au mois dernier";
    return `${sign}${change}% par rapport à ${comparison}`;
  };

  // Fonction pour obtenir l'icône selon le changement
  const getTrendIcon = (change: number | undefined) => {
    if (change === undefined) return TrendingUp; // Par défaut si pas encore calculé
    return change >= 0 ? TrendingUp : TrendingDown;
  };

  // Cartes de statistiques
  const statsCards = [
    {
      title: "Entreprises",
      value: stats?.totalCompanies || 0,
      change:
        stats?.companiesChange !== undefined
          ? formatChange(stats.companiesChange)
          : "Calcul en cours...",
      icon: Building2,
      trendIcon: getTrendIcon(stats?.companiesChange),
      color: "text-primary",
    },
    {
      title: "Utilisateurs",
      value: stats?.totalUsers || 0,
      change:
        stats?.usersChange !== undefined
          ? formatChange(stats.usersChange)
          : "Calcul en cours...",
      icon: Users,
      trendIcon: getTrendIcon(stats?.usersChange),
      color: "text-purple-600",
    },
    {
      title: "Sessions",
      value: stats?.sessionsThisMonth || 0,
      change:
        stats?.sessionsChange !== undefined
          ? formatChange(stats.sessionsChange)
          : "Calcul en cours...",
      icon: Clock,
      trendIcon: getTrendIcon(stats?.sessionsChange),
      color: "text-orange-600",
    },
    {
      title: "Pointages d'aujourd'hui",
      value: stats?.totalSessionsToday || 0,
      change:
        stats?.sessionsTodayChange !== undefined
          ? formatChange(stats.sessionsTodayChange, true)
          : "Calcul en cours...",
      icon: Clock,
      trendIcon: getTrendIcon(stats?.sessionsTodayChange),
      color: "text-green-600",
    },
  ];

  // Formater une date relative
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) return "Il y a 1 jour";
    if (diffDays < 7) return `Il y a ${diffDays} jours`;
    if (diffDays < 14) return "Il y a 1 semaine";
    return `Il y a ${Math.floor(diffDays / 7)} semaines`;
  };

  // ========== C. Gestion des erreurs et états de chargement ==========
  // Afficher le skeleton pendant le chargement
  if (loading || loadingCompanies) {
    return (
      <div className="p-4 sm:p-6">
        <div className="space-y-4">
          <div>
            <Skeleton className="h-8 w-48 mb-2" />
            <Skeleton className="h-4 w-64" />
          </div>
          <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-32 rounded-md" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Afficher l'erreur si la requête a échoué
  if (errorStats || errorCompanies) {
    return (
      <div className="p-4 sm:p-6">
        <div className="lg:ml-0">
          <div className="bg-red-50 text-red-600 p-4 rounded-lg text-sm sm:text-base">
            Erreur : {errorStats?.message || errorCompanies?.message}
          </div>
        </div>
      </div>
    );
  }

  // ========== D. JSX ==========
  return (
    <div className="space-y-4 sm:space-y-6">
      {/* En-tête */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-3">
          <LayoutDashboard className="h-8 w-8 text-primary" />
          Dashboard
        </h1>
        <p className="text-sm sm:text-base text-muted-foreground">
          Vue d'ensemble de votre activité
        </p>
      </div>

      {/* Cartes de statistiques */}
      <div className="grid gap-4 sm:gap-6 grid-cols-2 lg:grid-cols-4">
        {statsCards.map((stat) => (
          <Card key={stat.title} className="p-4 sm:p-6">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs sm:text-sm font-medium text-muted-foreground">
                {stat.title}
              </CardTitle>
              <stat.icon
                className={`h-4 w-4 sm:h-5 sm:w-5 ${stat.color} shrink-0`}
              />
            </CardHeader>
            <CardContent>
              <div className="text-xl sm:text-2xl lg:text-3xl font-bold">
                {stat.value}
              </div>
              <div className="flex items-center gap-1 mt-1">
                <stat.trendIcon
                  className={`h-3 w-3 shrink-0 ${
                    stat.trendIcon === TrendingDown
                      ? "text-red-600"
                      : "text-green-600"
                  }`}
                />
                <p className="text-xs text-muted-foreground line-clamp-2">
                  {stat.change}
                </p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Graphiques et dernières entreprises */}
      <div className="grid gap-4 sm:gap-6 lg:grid-cols-2">
        {/* Graphique des pointages */}
        <Card className="p-4 sm:p-6">
          <h2 className="mb-4 text-lg sm:text-xl font-semibold">
            Pointages des 7 derniers jours
          </h2>
          <WeeklyChart />
        </Card>

        {/* 5 dernières entreprises créées */}
        <Card className="p-4 sm:p-6">
          <h2 className="mb-4 text-lg sm:text-xl font-semibold">
            Dernières entreprises créées
          </h2>
          <div className="space-y-2 sm:space-y-3">
            {recentCompanies.map((company) => (
              <div
                key={company.id}
                className="flex items-center justify-between rounded-lg border p-2 sm:p-3 transition-colors hover:bg-muted/50"
              >
                <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                  <div className="flex h-8 w-8 sm:h-10 sm:w-10 items-center justify-center rounded-lg bg-primary/10 text-primary shrink-0">
                    <Building2 className="h-4 w-4 sm:h-5 sm:w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm sm:text-base truncate">
                      {company.name}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatDate(company.createdAt)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Graphique d'évolution mensuelle */}
      <Card className="p-4 sm:p-6">
        <h2 className="mb-4 text-lg sm:text-xl font-semibold">
          Évolution des sessions (6 derniers mois)
        </h2>
        <MonthlyChart />
      </Card>
    </div>
  );
};
